/**
 * Módulo Editor HTML
 * Renderiza HTML customizado do Strapi
 */

import BaseModule from './BaseModule.js';

export default class EditorHTMLModule extends BaseModule {
    static defaultConfig = {
        container: null,
        cssPrefix: 'mzk-editor-html'
    };

    constructor(config = {}) {
        super({ ...EditorHTMLModule.defaultConfig, ...config });
        this.data = null;
    }

    /**
     * Inicializa o módulo
     */
    async init() {
        if (!this.config.data) {
            throw new Error('EditorHTMLModule requer data na configuração');
        }

        this.data = this.config.data;
        this.initialized = true;
    }

    /**
     * Renderiza o HTML customizado
     */
    async render(container = null) {
        if (!this.initialized) {
            throw new Error('Módulo não foi inicializado. Chame init() primeiro.');
        }

        this.container = container || this.config.container;
        if (!this.container) {
            throw new Error('Container é obrigatório');
        }

        const html = this.buildHTML();
        this.container.innerHTML = html;

        // Executa scripts embutidos no HTML (aguarda scripts externos)
        await this.executeScripts();
    }

    /**
     * Constrói HTML do editor
     * @private
     */
    buildHTML() {
        const { cssPrefix } = this.config;
        const { content } = this.data;

        return `
      <div class="${cssPrefix}-container">
        ${content}
      </div>
    `;
    }

    /**
     * Executa scripts presentes no HTML injetado
     * @private
     */
    async executeScripts() {
        const container = this.container.querySelector(`.${this.config.cssPrefix}-container`);
        if (!container) {
            console.warn('[EditorHTMLModule] Container não encontrado para executar scripts');
            return;
        }

        // Encontra todos os scripts no container
        const scripts = Array.from(container.querySelectorAll('script'));

        // Processa scripts em sequência (aguarda externos carregarem)
        for (let index = 0; index < scripts.length; index++) {
            const oldScript = scripts[index];

            await new Promise((resolve, reject) => {
                const newScript = document.createElement('script');

                // Copia todos os atributos (type, src, etc)
                Array.from(oldScript.attributes).forEach(attr => {
                    newScript.setAttribute(attr.name, attr.value);
                });

                // Se tem src (script externo)
                if (oldScript.src) {
                    newScript.src = oldScript.src;

                    // Aguarda o script externo carregar antes de continuar
                    newScript.onload = () => resolve();
                    newScript.onerror = (error) => {
                        console.error(`[EditorHTMLModule] Erro ao carregar script externo ${index + 1}:`, error);
                        resolve(); // Continua mesmo se falhar
                    };
                } else {
                    // Script inline - executa imediatamente
                    let scriptContent = oldScript.textContent || oldScript.innerHTML;

                    // Detecta e remove DOMContentLoaded wrapper
                    // Padrão: document.addEventListener("DOMContentLoaded", function () { ... });
                    const domContentLoadedRegex = /document\.addEventListener\s*\(\s*["']DOMContentLoaded["']\s*,\s*function\s*\([^)]*\)\s*\{([\s\S]*)\}\s*\)\s*;?\s*$/;
                    const domContentLoadedArrowRegex = /document\.addEventListener\s*\(\s*["']DOMContentLoaded["']\s*,\s*\([^)]*\)\s*=>\s*\{([\s\S]*)\}\s*\)\s*;?\s*$/;

                    let match = scriptContent.match(domContentLoadedRegex);
                    if (match) {
                        // Extrai o conteúdo interno e envolve em IIFE auto-executável
                        scriptContent = `(function() {${match[1]}})();`;
                    } else {
                        match = scriptContent.match(domContentLoadedArrowRegex);
                        if (match) {
                            scriptContent = `(function() {${match[1]}})();`;
                        }
                    }

                    newScript.textContent = scriptContent;

                    // Script inline resolve imediatamente
                    resolve();
                }

                // Substitui o script antigo pelo novo (isso executa o script)
                try {
                    oldScript.parentNode.replaceChild(newScript, oldScript);
                } catch (error) {
                    console.error(`[EditorHTMLModule] Erro ao executar script ${index + 1}:`, error);
                    resolve();
                }
            });
        }
    }

    /**
     * Retorna CSS do módulo
     */
    static getStyles() {
        return `
      .mzk-editor-html-container {
        width: 100%;
        margin: 0;
      }

      .mzk-editor-html-container > * {
        max-width: 100%;
      }
    `;
    }

    /**
     * Destrói o módulo
     */
    destroy() {
        if (this.container) {
            this.container.innerHTML = '';
        }
        this.data = null;
        super.destroy();
    }
}
